import React from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTable, faSlidersH } from '@fortawesome/free-solid-svg-icons'
import LightTooltip from 'shared/light-tooltip'

// Plain-English, read-only summary of a custom-analytic recipe tree —
// "Flags transactions where all of the following are true: …". The full
// param-by-param detail lives in the recipe editor (ComposeAnalyticModal /
// the admin recipe builder); this is the at-a-glance explanation for the
// analytic show page and compose modal.
//
// Column and numeric-configuration params render as the same badges
// shared/render-description uses for core analytics ([col]/[cfg] markers),
// so custom and core descriptions read consistently.
//
// `predicates` is the slice of Composable::PredicateRegistry the backend
// embeds in the analytic show response (only the predicate types referenced
// by this recipe), so labels and param ui_labels render without a separate
// registry fetch.

const OP_PHRASES = {
  AND: 'all of the following are true',
  OR: 'any of the following are true',
  NOT: 'the following is NOT true'
}

const COLUMN_PARAM_TYPES = ['column', 'column_list', 'column_or_list']
const CONFIG_PARAM_TYPES = ['number', 'integer']

const badgeStyle = { fontSize: '0.85em', cursor: 'help' }
const badgeClass = 'badge bg-info bg-opacity-10 text-info-emphasis fw-normal'

const formatValue = (value) => {
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'boolean') return value ? 'yes' : 'no'
  if (typeof value === 'number') return value.toLocaleString()
  return String(value)
}

const ColumnBadges = ({ value }) => (
  <>
    {(Array.isArray(value) ? value : [value]).map((col, idx) => (
      <React.Fragment key={`${col}-${idx}`}>
        {idx > 0 && ' '}
        <LightTooltip content="Table Column">
          <span className={badgeClass} style={badgeStyle}>
            <FontAwesomeIcon icon={faTable} className="me-1" size="xs" />
            {String(col)}
          </span>
        </LightTooltip>
      </React.Fragment>
    ))}
  </>
)
ColumnBadges.propTypes = { value: PropTypes.any }

const ConfigBadge = ({ label, value }) => (
  <LightTooltip content={<span>{label}: <strong>{formatValue(value)}</strong></span>}>
    <span className={badgeClass} style={badgeStyle}>
      <FontAwesomeIcon icon={faSlidersH} className="me-1" size="xs" />
      {formatValue(value)}
    </span>
  </LightTooltip>
)
ConfigBadge.propTypes = { label: PropTypes.string, value: PropTypes.any }

const ParamValue = ({ param, value }) => {
  if (COLUMN_PARAM_TYPES.includes(param.type)) return <ColumnBadges value={value} />
  if (CONFIG_PARAM_TYPES.includes(param.type)) {
    return <ConfigBadge label={param.ui_label || param.name} value={value} />
  }
  return <>{formatValue(value)}</>
}
ParamValue.propTypes = { param: PropTypes.object.isRequired, value: PropTypes.any }

const LeafSummary = ({ node, predicates }) => {
  const schema = predicates?.[node.type]
  if (!schema) {
    return <>Unknown check (<code>{node.type}</code>)</>
  }

  const setParams = (schema.params || [])
    .filter((p) => node[p.name] !== undefined && node[p.name] !== null && node[p.name] !== '')

  if (setParams.length === 0) return <>{schema.label}</>

  return (
    <>
      {schema.label}{' ('}
      {setParams.map((p, idx) => (
        <React.Fragment key={p.name}>
          {idx > 0 && ', '}
          {(p.ui_label || p.name).toLowerCase()}: <ParamValue param={p} value={node[p.name]} />
        </React.Fragment>
      ))}
      {')'}
    </>
  )
}
LeafSummary.propTypes = { node: PropTypes.object.isRequired, predicates: PropTypes.object }

const childrenOf = (node) => (
  node.op === 'NOT' ? [node.predicate].filter(Boolean) : node.predicates || []
)

const NodeSummary = ({ node, predicates }) => {
  if (!node || typeof node !== 'object') return null
  if (node.type) return <li><LeafSummary node={node} predicates={predicates} /></li>
  return (
    <li>
      <span className="text-muted">{OP_PHRASES[node.op] || node.op}:</span>
      <ul className="mb-0">
        {childrenOf(node).map((child, idx) => (
          <NodeSummary key={idx} node={child} predicates={predicates} />
        ))}
      </ul>
    </li>
  )
}
NodeSummary.propTypes = { node: PropTypes.object, predicates: PropTypes.object }

const RecipePlainSummary = ({ recipe, predicates }) => {
  if (!recipe) {
    return <div className="text-muted small">No recipe defined.</div>
  }

  if (recipe.type) {
    return (
      <p className="mb-0">
        Flags transactions where: <LeafSummary node={recipe} predicates={predicates} />.
      </p>
    )
  }

  return (
    <div>
      <p className="mb-1">
        Flags transactions where <span className="fw-semibold">{OP_PHRASES[recipe.op] || recipe.op}</span>:
      </p>
      <ul className="mb-0">
        {childrenOf(recipe).map((child, idx) => (
          <NodeSummary key={idx} node={child} predicates={predicates} />
        ))}
      </ul>
    </div>
  )
}

RecipePlainSummary.propTypes = {
  recipe: PropTypes.object,
  predicates: PropTypes.object
}

export default RecipePlainSummary
